<template>
  <div class="fullscreen-center flex flex-col items-center justify-center">
    <!-- Authenticator Code Modal -->
    <div v-if="!showBackupCode" class="flex flex-col items-center">
      <h2 class="verification-title mb-4">Verify it's you</h2>
      <div class="bg-surface-2 mb-4 p-4 rounded-xl w-full" style="max-width: 400px;">
        
        <div class="verification-content">
          <h3 class="auth-method-title">Authentication code</h3>
          <p class="auth-instructions">Enter the 6-digit code from your authenticator app</p>
          
          <div class="code-inputs">
            <input 
              v-for="(d, i) in digits" 
              :key="i" 
              :ref="el => (digitRefs[i] = el)" 
              class="digit" 
              :class="{ 'error': showError }"
              type="text"
              maxlength="1" 
              v-model="digits[i]" 
              @input="onDigitInput(i, $event)"
              @keydown.backspace.prevent="onBackspace(i, $event)" 
              @paste="onPaste(i, $event)"
              :autofocus="i === 0"
            >
          </div>
          
          <div v-if="showError" class="error-message">
            <div v-if="failedAttempts < 3" class="error-text">
              Incorrect code. Please try again.
            </div>
            <div v-else class="error-text">
              You've entered the wrong code multiple times.<br>
              You can try again or use a backup code instead.
            </div>
          </div>
          
           <div class="button-row">
             <FormButton type="main" class="basis-1/2 text-center justify-center" @click="verifyCode" :disabled="!isCodeComplete || processing">  {{ processing ? 'Verifying...' : 'Verify' }} </FormButton>
             
             <FormButton type="secondary" class="basis-1/2 text-center justify-center" @click="() => { showBackupCode = true; failedAttempts = 0; showError = false; }" v-if="!showBackupCode">
               Use backup code
             </FormButton>
           </div>
           
           <div class="w-full">
             <FormButton class="return-login-btn" @click="backToLogin">
               Return to login
             </FormButton>
           </div>
         </div>
       </div>
     </div>
    

    <!-- Backup Code Modal -->
    <div v-else class="flex flex-col items-center">
      <h2 class="verification-title mb-4">Verify it's you</h2>
      <div class="bg-surface-2 mb-4 p-4 rounded-xl w-full" style="max-width: 400px;">
        <div class="verification-content">
          <h3 class="auth-method-title">Backup code</h3>
          <p class="auth-instructions">Enter your backup code</p>
          
          <div class="w-full">
            <input 
              class="backup-input w-full" 
              :class="{ 'error': showError }"
              type="text" 
              v-model="backupCode"
              placeholder="Enter backup code"
              @keypress.enter="verifyBackupCode"
            >
          </div>
            
          <div v-if="showError" class="error-message">
            <div v-if="failedAttempts < 3" class="error-text">
              Incorrect backup code. Please try again.
            </div>
            <div v-else class="error-text">
              You've entered the wrong backup code multiple times.<br>
              Please contact support if you continue to have issues.
            </div>
          </div>
            
           <div class="button-row">
             <FormButton type="main" class="basis-1/2" :disabled="!backupCode || processing"  @click="verifyBackupCode"> {{ processing ? 'Verifying...' : 'Verify' }}</FormButton>
           </div>
           
           <div class="w-full">
             <FormButton class="return-login-btn" @click="backToLogin">
               Return to login
             </FormButton>
           </div>
          </div>
        </div>
      </div>
     
    </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useNotification } from "@kyvg/vue3-notification"
import UserService from '@/services/UserService'
import FormButton from '@/components/Atoms/FormButton.vue';
import { track, EVENTS } from '@/lib/tracking'
import { redirectAfterAuth } from '@/Utils/authRedirect'

defineOptions({ name: 'TwoFactorVerification' })

const router = useRouter()
const { notify } = useNotification()

const minWindowHeight = ref(null)
const showBackupCode = ref(false)
const processing = ref(false)
const digits = ref(['', '', '', '', '', ''])
const digitRefs = ref([])
const backupCode = ref('')
const failedAttempts = ref(0)
const showError = ref(false)

const isCodeComplete = computed(() => digits.value.every(v => v && v.length === 1))
const code = computed(() => digits.value.join(''))

onMounted(() => {
  minWindowHeight.value = window.innerHeight + "px"
  
  // Check if user has temporary session data, redirect to login if not
  const tempAuthKey = localStorage.getItem('token')
  const twoFactorRequired = localStorage.getItem('twoFactorRequired')
  const user = UserService.getUser()
  
  if (!tempAuthKey || (!twoFactorRequired && !user?.twoFactorEnabled)) {
    router.push('/login')
  }
})

const onDigitInput = (index, evt) => {
  const value = (evt.target.value || '').replace(/\D/g, '').slice(0, 1)
  digits.value[index] = value
  if (value && index < digits.value.length - 1 && digitRefs.value[index + 1]) {
    digitRefs.value[index + 1].focus()
  }
}

const onBackspace = (index) => {
  if (!digits.value[index] && index > 0 && digitRefs.value[index - 1]) {
    digitRefs.value[index - 1].focus()
    digits.value[index - 1] = ''
  } else {
    digits.value[index] = ''
  }
}

const onPaste = (index, evt) => {
  evt.preventDefault()
  const paste = (evt.clipboardData || window.clipboardData).getData('text') || ''
  const chars = paste.replace(/\D/g, '').slice(0, digits.value.length - index).split('')
  if (!chars.length) return
  for (let i = 0; i < chars.length; i++) {
    digits.value[index + i] = chars[i]
  }
  const nextIndex = Math.min(index + chars.length, digits.value.length - 1)
  if (digitRefs.value[nextIndex]) digitRefs.value[nextIndex].focus()
}

const verifyCode = async () => {
  if (!isCodeComplete.value) return
  
  processing.value = true
  showError.value = false
  
  try {
    // Complete the login process
    const tempAuthKey = localStorage.getItem('token')
    const response = await UserService.twoFactorVerify(tempAuthKey, code.value)
    
    if (tempAuthKey && response.user) {
      UserService.setSession(response.user, response.authKey)
      localStorage.setItem('twoFactorValidated', true)
      localStorage.removeItem('twoFactorRequired')
    }
    
    // Reset failed attempts on success
    failedAttempts.value = 0
    track(EVENTS.TWO_FACTOR_VERIFIED, { method: 'authenticator' })
    notify({ group: 'success', text: '2FA verification successful' })
    router.push('/dashboard')
  } catch (error) {
    // Increment failed attempts
    failedAttempts.value++
    showError.value = true
    
    // Clear the digits on error
    digits.value = ['', '', '', '', '', '']
    if (digitRefs.value[0]) digitRefs.value[0].focus()
  }
  processing.value = false
}

const verifyBackupCode = async () => {
  if (!backupCode.value) return
  
  processing.value = true
  showError.value = false
  
  try {
    // Complete the login process
    const tempAuthKey = localStorage.getItem('token')
    const response = await UserService.twoFactorVerify(tempAuthKey, backupCode.value)
    
    if (tempAuthKey && response.user) {
      UserService.setSession(response.user, response.authKey)
      localStorage.setItem('twoFactorValidated', true)
      localStorage.removeItem('twoFactorRequired')
    }
    
    // Reset failed attempts on success
    failedAttempts.value = 0
    track(EVENTS.TWO_FACTOR_VERIFIED, { method: 'backup_code' })
    notify({ group: 'success', text: 'Backup code verification successful' })
    router.push('/dashboard')
  } catch (error) {
    // Increment failed attempts for backup code too
    failedAttempts.value++
    showError.value = true
    backupCode.value = ''
  }
  processing.value = false
}

const backToLogin = () => {
  localStorage.removeItem('twoFactorRequired')
  localStorage.removeItem('token')
  router.back()
}
</script>

<style scoped>
.view-wrapper {
  background: #101624;
  min-height: 100vh;
}

.fullscreen-center {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
}

.verification-card {
  background: #1C2438;
  border-radius: 12px;
  padding: 40px;
  width: 100%;
  max-width: 400px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.verification-title {
  color: #fff;
  font-size: 24px;
  font-weight: 600;
  text-align: center;
  margin-bottom: 30px;
}

.verification-content {
  text-align: center;
}

.auth-method-title {
  color: #fff;
  font-size: 18px;
  font-weight: 500;
  margin-bottom: 8px;
}

.auth-instructions {
  color: #B8BCC8;
  font-size: 14px;
  margin-bottom: 30px;
  line-height: 1.4;
}

.code-inputs {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 12px;
  margin-bottom: 30px;
}

.digit {
  height: 48px;
  width: 48px;
  text-align: center;
  font-size: 18px;
  font-weight: 500;
  border-radius: 8px;
  border: 1px solid #3A4151;
  background: #2A3142;
  color: #fff;
  transition: all 0.2s ease;
}

.digit:focus {
  outline: none;
  border-color: #4A90E2;
  background: #3A4151;
}

.digit:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.digit.error {
  border-color: #E74C3C;
  background: #3A1F1F;
}

.error-message {
  margin-bottom: 20px;
}

.error-text {
  color: #E74C3C;
  font-size: 14px;
  text-align: center;
  line-height: 1.4;
}

.button-row {
  display: flex;
  gap: 12px;
}

.verify-btn {
  flex: 1;
  height: 48px;
  background: #4A90E2;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.verify-btn:hover:not(:disabled) {
  background: #357ABD;
}

.verify-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.backup-btn {
  flex: 1;
  height: 48px;
  background: #2A3142;
  color: #fff;
  border: 1px solid #3A4151;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.backup-btn:hover {
  background: #3A4151;
  border-color: #4A90E2;
}

.return-login-btn {
  background: none;
  border: none;
  color: #4A90E2;
  font-size: 14px;
  cursor: pointer;
  text-decoration: underline;
  transition: color 0.2s ease;
  margin-top: 20px;
}

.return-login-btn:hover {
  color: #357ABD;
}

.backup-input {
  width: calc(6 * 48px + 5 * 12px) !important; /* Same width as 6 digits + 5 gaps */
  max-width: 100%;
  height: 48px;
  padding: 0 16px;
  background: #2A3142;
  border: 1px solid #3A4151;
  border-radius: 8px;
  color: #fff;
  font-size: 16px;
  margin-bottom: 30px;
  transition: all 0.2s ease;
  box-sizing: border-box;
}

.backup-input:focus {
  outline: none;
  border-color: #4A90E2;
  background: #3A4151;
}

.backup-input.error {
  border-color: #E74C3C;
  background: #3A1F1F;
}

.backup-input::placeholder {
  color: #B8BCC8;
}
</style>
